/**
 * Configuration Types
 * 
 * Type definitions for all configuration data structures.
 */

// ============================================================================
// Symbol Classification Types
// ============================================================================

/**
 * A symbol pattern that can be either a regex or exact string match
 */
export interface SymbolPattern {
  /** Regex pattern for flexible matching */
  pattern: RegExp;
  /** Human-readable description */
  description?: string;
}

/**
 * Symbol classification category
 */
export enum SymbolCategory {
  Treasury = 'treasury',
  Municipal = 'municipal',
  Reit = 'reit',
  Mlp = 'mlp',
  MoneyMarket = 'moneyMarket',
}

// ============================================================================
// Tax Configuration Types
// ============================================================================

/**
 * A single tax bracket
 */
export interface TaxBracket {
  /** Minimum income for this bracket (inclusive) */
  min: number;
  /** Maximum income for this bracket (exclusive) */
  max: number;
  /** Tax rate as decimal (e.g., 0.22 for 22%) */
  rate: number;
}

/**
 * Filing status options
 */
export enum FilingStatus {
  Single = 'single',
  MarriedFilingJointly = 'marriedFilingJointly',
  MarriedFilingSeparately = 'marriedFilingSeparately',
  HeadOfHousehold = 'headOfHousehold',
}

/**
 * Federal tax configuration for a single year
 */
export interface FederalTaxConfig {
  /** Ordinary income brackets by filing status */
  brackets: Record<FilingStatus, TaxBracket[]>;
  /** Long-term capital gains brackets by filing status */
  capitalGainsBrackets: Record<FilingStatus, TaxBracket[]>;
  /** Standard deduction by filing status */
  standardDeductions: Record<FilingStatus, number>;
  /** NIIT thresholds by filing status */
  niitThresholds: Record<FilingStatus, number>;
  /** NIIT rate (currently 3.8%) */
  niitRate: number;
  /** Social Security wage base */
  socialSecurityWageBase: number;
  /** Social Security rate */
  socialSecurityRate: number;
  /** Medicare rate */
  medicareRate: number;
  /** Additional Medicare rate for high earners */
  additionalMedicareRate: number;
  /** Additional Medicare threshold by filing status */
  additionalMedicareThresholds: Record<FilingStatus, number>;
  /** AMT exemption amounts by filing status */
  amtExemptions: Record<FilingStatus, number>;
  /** AMT phase-out thresholds by filing status */
  amtPhaseOutThresholds: Record<FilingStatus, number>;
  /** AMT exemption phase-out rate (25% through 2025, 50% from 2026 under OBBBA) */
  amtPhaseOutRate: number;
  /** AMT 26%/28% rate breakpoint by filing status */
  amtHighRateThreshold: Record<FilingStatus, number>;
  /** AMT rates: [lower rate, higher rate] e.g. [0.26, 0.28] */
  amtRates: [number, number];
  /** 401(k) elective deferral limit */
  retirement401kLimit: number;
  /** 401(k) catch-up contribution limit (age 50+) */
  retirement401kCatchup: number;
  /** Itemized-deduction rules (SALT cap and OBBBA changes, TAX-14). */
  itemizedDeductions: ItemizedDeductionRules;
}

/**
 * Itemized-deduction rules for a year (IRC §164(b)(6) as amended by the One
 * Big Beautiful Bill Act; Rev. Proc. 2025-32). Married filing separately gets
 * HALF of `saltCap`, `saltPhaseDownThreshold` and `saltFloor`.
 */
export interface ItemizedDeductionRules {
  /** State and local tax deduction cap. */
  saltCap: number;
  /** MAGI above which the cap phases down; null = no phase-down. */
  saltPhaseDownThreshold: number | null;
  /** Share of MAGI above the threshold that reduces the cap. */
  saltPhaseDownRate: number;
  /** The phase-down never takes the cap below this. */
  saltFloor: number;
  /** Itemized charitable gifts are reduced by this fraction of AGI (0.5% from 2026). */
  charitableAgiFloorRate: number;
  /** Charitable deduction for NON-itemizers (from 2026); 0 = none. MFS/HOH use `single`. */
  nonItemizerCharitableCap: { single: number; marriedFilingJointly: number };
  /**
   * Limitation on itemized deductions for 37%-bracket filers (from 2026):
   * reduce by this rate (2/37) × the lesser of the itemized deductions and
   * the income above the 37% bracket start; 0 = none.
   */
  itemizedBenefitLimitRate: number;
}

/**
 * State tax configuration
 */
export interface StateTaxConfig {
  /** Full state name */
  name: string;
  /** Two-letter state code */
  code: string;
  /** Top marginal tax rate as decimal */
  rate: number;
  /** Standard deduction by filing status (if applicable) */
  standardDeductions?: Partial<Record<FilingStatus, number>>;
  /** Whether the state has a flat tax */
  isFlat?: boolean;
  /** State tax brackets (if progressive) */
  brackets?: Record<FilingStatus, TaxBracket[]>;
}

/**
 * Complete tax configuration for a single year
 */
export interface TaxYearConfig {
  year: number;
  federal: FederalTaxConfig;
  states: Record<string, StateTaxConfig>;
}

// ============================================================================
// Import Provider Types
// ============================================================================

/**
 * Mapping from provider-specific action string to standard action type
 */
export interface ActionMapping {
  /** The raw action string from the provider */
  raw: string;
  /** The standardized action type */
  standard: string;
  /** Optional description */
  description?: string;
}


/**
 * Company name to ticker symbol mapping
 */
export interface CompanyTickerMapping {
  /** Company name or abbreviation (case-insensitive key) */
  name: string;
  /** Ticker symbol */
  ticker: string;
}
